import { address as addressModel, Verbiage } from "@repo/models";

import * as constants from "./constants";
import { hasValue } from "./general";

const TIME_ONLY = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;

const currentYear = new Date().getFullYear();
const JANUARY = new Date(Date.UTC(currentYear, 0, 1));
const JULY = new Date(Date.UTC(currentYear, 6, 1));

/**
 * Formats like toLocaleDateString/toLocaleTimeString(undefined, options) without building a formatter per call,
 * which V8 only avoids when no options are passed. A formatter keeps the time zone it was built in, so it is
 * rebuilt when the system time zone's offset changes at either probe instant; zones that share both are not told apart.
 */
function localFormatter(options: Intl.DateTimeFormatOptions) {
	let formatter: Intl.DateTimeFormat | undefined;
	let januaryOffset: number | undefined;
	let julyOffset: number | undefined;
	return (date: Date) => {
		if (isNaN(date.getTime())) return "Invalid Date";
		const currentJanuaryOffset = JANUARY.getTimezoneOffset();
		const currentJulyOffset = JULY.getTimezoneOffset();
		if (!formatter || currentJanuaryOffset !== januaryOffset || currentJulyOffset !== julyOffset) {
			formatter = new Intl.DateTimeFormat(undefined, options);
			januaryOffset = currentJanuaryOffset;
			julyOffset = currentJulyOffset;
		}
		return formatter.format(date);
	};
}

const formatShortDate = localFormatter({ month: "numeric", day: "numeric", year: "2-digit" });
const formatShortTime = localFormatter({ hour: "2-digit", minute: "2-digit" });

const currencyFormatter = (fractionDigits: number) =>
	new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: fractionDigits,
		maximumFractionDigits: fractionDigits,
	});
const currencyWithCents = currencyFormatter(2);
const currencyWholeDollars = currencyFormatter(0);

/**Formats name into a string */
function name(firstName: string, lastName: string, normal?: boolean) {
	if (!firstName || !lastName) return firstName || lastName;
	if (!normal) return `${lastName}, ${firstName}`;
	return `${firstName} ${lastName}`;
}

/** Formats address lines into string */
function address(addressLine1: string, addressLine2: string) {
	if (addressLine1 && addressLine2) return `${addressLine1} ${addressLine2}`;
	if (addressLine1) return addressLine1;
	return addressLine2;
}

/** Formats address lines into url encoded address object */
function addressForUrl(value: addressModel): addressModel {
	return {
		...value,
		address2: value.address2 ?? "",
	};
}

/** Format city and state info into string */
function cityState(city: string, state: string, zip: string | number) {
	let str = "";
	if (city && state) str = `${city}, ${state}`;
	else if (city) str = city;
	else if (state) str = state;

	if (zip) {
		if (str) str += " ";
		str += zip;
	}
	return str;
}

/** Format address info into single string */
function fullAddress(addressLine1: string, addressLine2: string, city: string, state: string, zip: string | number) {
	const addressLine = address(addressLine1, addressLine2);
	const cityLine = cityState(city, state, zip);
	if (addressLine && cityLine) return `${addressLine} ${cityLine}`;
	if (addressLine) return addressLine;
	return cityLine;
}

/** Format phone number into single string
 * NOTE: currently doesn't handle extensions
 */
function phone(number: string | number) {
	if (!number) return number;
	let onlyDigits = number.toString().replace(/\D/g, "");
	if (onlyDigits.length < 10) return onlyDigits;
	let formatted = "";
	if (onlyDigits.length > 10) {
		const extraDigits = onlyDigits.length - 10;
		formatted += "+";
		formatted += onlyDigits.slice(0, extraDigits);
		formatted += " ";
		onlyDigits = onlyDigits.slice(extraDigits);
	}
	formatted += "(" + onlyDigits.slice(0, 3) + ") ";
	formatted += onlyDigits.slice(3, 6);
	formatted += "-" + onlyDigits.slice(6);
	return formatted;
}

/** Format phone number into single string
 * NOTE: currently doesn't handle extensions
 */
function phoneOnlyNumeric(number: string) {
	if (!number) return number;
	return number.replace(/\D/g, "");
}

const fullTimeFromSeconds = (totalSeconds: number) => timeFromSeconds(totalSeconds, true);

/** Formats total seconds into hours and minutes and seconds */
function timeFromSeconds(totalSeconds: number, full?: boolean) {
	if (!hasValue(totalSeconds)) return null;
	const totalMinutes = Math.floor(totalSeconds / constants.SECONDS_IN_MINUTE);
	const hours = Math.floor(totalMinutes / constants.MINUTES_IN_HOUR);
	const minutes = totalMinutes % constants.MINUTES_IN_HOUR;
	const seconds = totalSeconds % constants.SECONDS_IN_MINUTE;

	const displayingHours = full || hours;

	const dispSeconds = (seconds < 10 ? "0" : "") + seconds;
	const dispMinutes = (displayingHours && minutes < 10 ? "0" : "") + minutes;
	let time = dispMinutes + ":" + dispSeconds;
	if (displayingHours) time = hours + ":" + time;
	return time;
}

/** Formats date into date-only string */
function dateOf(date: Date | string, fullYear = true) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	return fullYear ? date.toLocaleDateString() : formatShortDate(date);
}

/** Formats date into time-only string */
function timeOf(date: Date | string, displaySeconds = true) {
	if (!date) return null;
	if (typeof date === "string") {
		if (TIME_ONLY.test(date)) {
			const [hours, minutes, seconds] = date.split(":").map(Number);
			const d = new Date();
			d.setHours(hours, minutes, seconds || 0, 0);
			date = d;
		} else {
			date = new Date(date);
		}
	}
	return displaySeconds ? date.toLocaleTimeString() : formatShortTime(date);
}

/** Formats date into date/time string */
function dateTimeOf(date: Date | string, displaySeconds = true, fullYear = true) {
	if (!date) return null;
	return dateOf(date, fullYear) + " " + timeOf(date, displaySeconds);
}

const shortDateOf = (date: Date | string) => dateOf(date, false);

/** Date Time, without seconds */
const shortDateTimeOf = (date: Date | string) => dateTimeOf(date, false, false);

/** Holds date in original timezone */
function holdInUtc(date: Date | string) {
	if (!date) return null;
	if (typeof date === "string") date = new Date(date);
	const tzOffset = date.getTimezoneOffset();
	if (tzOffset) date.setMinutes(date.getMinutes() + tzOffset);
	return date;
}

/** Holds specified dates in original timezone */
function holdPropsInUtc<T extends Partial<Record<K, Date>>, K extends keyof T>(obj: T, ...props: K[]) {
	for (const prop of props) {
		const newValue = holdInUtc(obj[prop]);
		Object.assign(obj, { [prop]: newValue });
	}
}

/** Returns date in original timezone */
function originalDateOf(date: Date | string) {
	return dateOf(holdInUtc(date));
}
/**
 * Returns date, or null if date is empty or <1800
 */
function dummyNull(date: Date | string) {
	if (!date) return null;
	date = new Date(date);
	if (date.getFullYear() <= 1800) return null;
	return date;
}

/** Splits words on upper case, like SampleWordText => Sample Word Text */
function splitWordsOnUpperCase(str: string | undefined) {
	return str?.replace(/(?<=[A-Z])(?=[A-Z][a-z])|(?<=[^A-Z])(?=[A-Z])|(?<=[A-Za-z])(?=[^A-Za-z])/gm, " ");
}

function snakeToPascal(snakeCase: string): string {
	const words = snakeCase.split("_");
	return words
		.map((word, index) =>
			index === 0
				? word.charAt(0).toUpperCase() + word.slice(1)
				: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
		)
		.join("");
}

export function camelToPascal(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function camelToStartCase(str: string) {
	const pascal = camelToPascal(str);
	return splitWordsOnUpperCase(pascal);
}

function formatPercent(value: number) {
	return (value * 100).toFixed(0) + "%";
}

function currency(amount: number | string, includeDecimal = true) {
	let numericAmount: number;

	if (typeof amount === "string") {
		numericAmount = parseFloat(amount.replace(/,/g, ""));
	} else {
		numericAmount = amount ?? 0;
	}

	if (isNaN(numericAmount)) {
		numericAmount = 0;
	}

	return (includeDecimal ? currencyWithCents : currencyWholeDollars).format(numericAmount);
}

export function intToTime(hour: number) {
	if (!Number.isFinite(hour)) throw new RangeError("Invalid time value");
	const hourOfDay = ((Math.floor(hour) % constants.HOURS_IN_DAY) + constants.HOURS_IN_DAY) % constants.HOURS_IN_DAY;
	return `${hourOfDay % 12 || 12}${hourOfDay < 12 ? "am" : "pm"}`;
}

export function formatVerbiage(text: string, verbiage: Verbiage) {
	if (!text) return text;
	if (!verbiage) return text;

	const key = text.replaceAll(" ", "").toLowerCase();
	return Object.entries(verbiage).find(([verbiageKey]) => verbiageKey.toLowerCase() === key)?.[1] ?? text;
}

export {
	name,
	address,
	addressForUrl,
	cityState,
	fullAddress,
	phone,
	phoneOnlyNumeric,
	timeFromSeconds,
	fullTimeFromSeconds,
	dateOf,
	timeOf,
	dateTimeOf,
	shortDateTimeOf,
	shortDateOf,
	holdInUtc,
	holdPropsInUtc,
	originalDateOf,
	dummyNull,
	splitWordsOnUpperCase,
	snakeToPascal,
	formatPercent,
	currency,
};
